import React, { ReactNode, useRef, useState, TransitionEvent, useEffect } from 'react';

import './index.less';

import IbButton from '../IbButton';
import IbIcon from '../IbIcon';

import {
  COLLAPSED_HEIGHT,
  CONTENT_CLASS_NAME,
  MAIN_CLASS_NAME,
  MAX_HEIGHT_PROPERTY,
  SWITCH_BUTTON_CLASS_NAME,
} from './const';

interface IIbTextCollapseProps {
  content: ReactNode;
  contentId?: string | number;
  style?: 'default' | 'accent';
}

const IbTextCollapse: React.FC<IIbTextCollapseProps> = ({ content, contentId = '', style = 'default' }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(true);
  const [maxHeight, setMaxHeight] = useState(COLLAPSED_HEIGHT);

  const reset = () => {
    setExpanded(false);
    setClamped(true);
    setMaxHeight(COLLAPSED_HEIGHT);
  };

  useEffect(reset, [contentId]);

  const toggleCollapsedState = () => {
    if (!contentRef.current) {
      return;
    }

    if (expanded) {
      // collapsing
      setMaxHeight(`${contentRef.current.scrollHeight}px`);

      requestAnimationFrame(() => {
        setMaxHeight(COLLAPSED_HEIGHT);
      });

      setExpanded(false);
    } else {
      // expanding
      setClamped(false);

      requestAnimationFrame(() => {
        if (!contentRef.current) {
          return;
        }

        setMaxHeight(`${contentRef.current.scrollHeight}px`);
        setExpanded(true);
      });
    }
  };

  const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.propertyName !== MAX_HEIGHT_PROPERTY) {
      return;
    }

    if (!expanded) {
      setClamped(true);
    } else {
      setMaxHeight('none');
    }
  };

  const mainClasses = [MAIN_CLASS_NAME, `${MAIN_CLASS_NAME}_${style}`];

  const contentClasses = [CONTENT_CLASS_NAME, `${CONTENT_CLASS_NAME}_${expanded ? 'expanded' : 'collapsed'}`];
  clamped && contentClasses.push(`${CONTENT_CLASS_NAME}_clamped`);

  const switchButtonClassNames = [SWITCH_BUTTON_CLASS_NAME];
  expanded && switchButtonClassNames.push(`${SWITCH_BUTTON_CLASS_NAME}_expanded`);

  return (
    <div className={mainClasses.join(' ')} onClick={toggleCollapsedState}>
      <div
        ref={contentRef}
        className={contentClasses.join(' ')}
        style={{
          maxHeight: maxHeight,
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {content}
      </div>
      <IbButton
        className={switchButtonClassNames.join(' ')}
        icon={<IbIcon iconName="right" size={20} />}
        type="icon"
        onClick={toggleCollapsedState}
      />
    </div>
  );
};

export default IbTextCollapse;
